/**
 * The fakes, one table for every place that lists them (HOW TO PLAY › FAKES, lesson 8's "four more" card, and the
 * pause MOVE LIST's tags, checked by fakes.test.ts): the same name, the same inputs, the same cost everywhere.
 * (The three used to disagree: HESITATION was "A → D" in one and "LET GO › D" in the other, FAST-FALL read like a
 * FAKE JUMP, and the move list said every fake costs stamina while three are free.)
 *
 * Inputs are for a head facing right (▶ = forward): `lead` comes before the caps — 'dash' = double-tap forward,
 * 'stop' = let go of the stick first, 'air' = while in the air.
 */
import { STAMINA, type ButtonName } from '@hf/shared';
import type { KeyMode } from './catalog';

export interface FakeInfo {
  name: string;
  keys: ButtonName[];
  mode: KeyMode;
  lead?: 'dash' | 'stop' | 'air';
  /** What it baits (a sentence fragment: "Baits …"). */
  baits: string;
  /** Stamina cost (0 = free). */
  cost: number;
  /** Extra rule shown with the cost. */
  limit?: string;
  /** Who uses it. */
  side: 'carrier' | 'qb' | 'defense' | 'pre-snap';
  /** Drilled in lesson 8 (the rest are "try them in PRACTICE"). */
  drilled: boolean;
}

export const FAKES: readonly FakeInfo[] = [
  {
    name: 'FAKE JUMP',
    keys: ['U', 'D'],
    mode: 'then',
    baits: 'a LEAP: then truck under him',
    cost: STAMINA.fakeJumpCancel,
    side: 'carrier',
    drilled: true,
  },
  {
    name: 'STUTTER',
    keys: ['L'],
    mode: 'tap',
    lead: 'dash',
    baits: 'a DIVE: dash at him, tap back',
    cost: STAMINA.stutter,
    side: 'carrier',
    drilled: true,
  },
  {
    name: 'HESITATION',
    keys: ['R'],
    mode: 'tap',
    lead: 'stop',
    baits: 'an over-eager chaser: stop dead, then burst',
    cost: 0,
    side: 'carrier',
    drilled: false,
  },
  {
    name: 'SPIN',
    keys: ['D', 'A'],
    mode: 'chord',
    baits: 'any lunge: you spin through it',
    cost: STAMINA.spin,
    side: 'carrier',
    drilled: false,
  },
  {
    name: 'PUMP FAKE',
    keys: ['A', 'D'],
    mode: 'holdWhile',
    baits: 'a jump for the ball',
    cost: STAMINA.pumpFake,
    limit: '1 PER PLAY',
    side: 'qb',
    drilled: true,
  },
  {
    name: 'HARD COUNT',
    keys: ['U'],
    mode: 'tap',
    baits: 'an early jump: OFFSIDE, 5 free yards',
    cost: 0,
    limit: '2 PER PLAY',
    side: 'pre-snap',
    drilled: true,
  },
  {
    name: 'FAKE DIVE',
    keys: ['A', 'D'],
    mode: 'then',
    baits: 'a hurdle: then LEAP him',
    cost: STAMINA.fakeDive,
    side: 'defense',
    drilled: false,
  },
  {
    name: 'FAST-FALL',
    keys: ['D'],
    mode: 'tap',
    lead: 'air',
    baits: 'a catch: slam down from a jump',
    cost: 0,
    side: 'defense',
    drilled: false,
  },
];

/** "20 STAMINA", "FREE · 2 PER PLAY"… */
export const fakeCost = (f: FakeInfo): string =>
  [f.cost > 0 ? `${f.cost} STAMINA` : 'FREE', ...(f.limit ? [f.limit] : [])].join(' · ');

/** The fakes lesson 8 does not drill. */
export const MORE_FAKES: readonly FakeInfo[] = FAKES.filter((f) => !f.drilled);
